import { formatShipmentDate } from "./shipment-date-display";
import { Card, CardBody, CardHeader, Chip } from "@heroui/react";
import type { Shipment, ShipmentPackage } from "@/api.types.v2";
import { useTranslation } from "react-i18next";
import { ShipmentStatusBadge } from "@/components/shipment-status-badge";
import { ShipmentRevisionHistory } from "./shipment-revision-history";

function formatCarrier(code?: string): string {
  if (!code) return "";
  return code
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getShipmentParty(shipment: Shipment, type: "FROM" | "TO" | "RETURN") {
  const fromAddress = shipment.addresses?.find((a) => a.type === type);
  if (!fromAddress) return null;

  const addr = fromAddress.address;
  const parts = [
    addr?.line1,
    addr?.line2,
    addr?.city,
    addr?.province,
    addr?.postalCode,
    addr?.country,
  ].filter(Boolean);

  return {
    name:
      (fromAddress.contactName !== undefined
        ? fromAddress.contactName
        : fromAddress.name) ?? "",
    fullAddress: parts.join(", "),
    company:
      fromAddress.contactCompany !== undefined
        ? fromAddress.contactCompany
        : fromAddress.company,
    instructions: fromAddress.instructions,
    phone:
      fromAddress.contactPhone !== undefined
        ? fromAddress.contactPhone
        : fromAddress.phone,
    email:
      fromAddress.contactEmail !== undefined
        ? fromAddress.contactEmail
        : fromAddress.email,
  };
}

export function CustomerShipmentDetails({
  shipment,
  showHeading,
}: {
  shipment: Shipment;
  showHeading: boolean;
}) {
  const { t, i18n } = useTranslation("app");
  const sender = getShipmentParty(shipment, "FROM");
  const recipient = getShipmentParty(shipment, "TO");
  const returnParty = getShipmentParty(shipment, "RETURN");
  const packages = shipment.packages ?? [];
  const plan = shipment.fulfillmentPlan;
  const service =
    shipment.selectedService?.availability === "RETAINED_DISPLAY"
      ? shipment.selectedService.value.name
      : null;
  const location =
    plan?.inductionKind === "DISPATCH_HANDOFF"
      ? {
          name: plan.facilityDetailsVersion === 1 ? plan.dispatchName : null,
          address: plan.dispatchAddress,
        }
      : plan?.inductionKind === "CARRIER_HANDOFF"
        ? {
            name:
              plan.facilityDetailsVersion === 1
                ? plan.carrierDropoffName
                : null,
            address: plan.carrierDropoffAddress,
          }
        : null;
  const locationAddress = location?.address
    ? [
        location.address.line1,
        location.address.line2,
        location.address.city,
        location.address.province,
        location.address.postalCode,
        location.address.country,
      ]
        .filter(Boolean)
        .join(", ")
    : null;
  const date = (
    value: string | null | undefined,
    precision: "DATE" | "INSTANT" = "INSTANT"
  ) =>
    value
      ? formatShipmentDate(
          precision === "DATE" ? value.slice(0, 10) : value,
          precision,
          i18n.language,
          plan?.timeZone
        )
      : null;
  const deliveryPrecision =
    plan?.deliveredPrecision === "DATE" ? "DATE" : "INSTANT";
  const firstDelivery = date(plan?.deliveredFastest, deliveryPrecision);
  const lastDelivery = date(plan?.deliveredSlowest, deliveryPrecision);
  const declaredMoney = (cents: number) =>
    new Intl.NumberFormat(i18n.language === "zh" ? "zh-CN" : "en-CA", {
      style: "currency",
      currency: "CAD",
    }).format(cents / 100);

  return (
    <section
      className="min-w-0 space-y-4"
      aria-label={
        shipment.shipmentNumber != null
          ? `SHP-${shipment.shipmentNumber}`
          : t("orderDetails.shipment")
      }
    >
      {showHeading && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">
            SHP-{shipment.shipmentNumber ?? "…"}
          </h2>
          <ShipmentStatusBadge status={shipment.status} />
        </div>
      )}
      {shipment?.trackingNumber && (
        <Card shadow="none" className="border border-divider">
          <CardHeader className="pb-0">
            <span className="text-tiny font-semibold uppercase tracking-wider text-default-500">
              {t("orderDetails.tracking")}
            </span>
          </CardHeader>
          <CardBody>
            <a
              href={`/tracking/${shipment.trackingNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline font-mono text-lg"
            >
              {shipment.trackingNumber}
            </a>
          </CardBody>
        </Card>
      )}

      {shipment?.carrierCode && (
        <Card shadow="none" className="border border-divider">
          <CardHeader className="pb-0">
            <span className="text-tiny font-semibold uppercase tracking-wider text-default-500">
              {t("orderDetails.carrier")}
            </span>
          </CardHeader>
          <CardBody>
            <p>{formatCarrier(shipment.carrierCode)}</p>
          </CardBody>
        </Card>
      )}

      {(service ||
        plan ||
        shipment.options?.signatureRequired ||
        shipment.options?.insurance) && (
        <Card shadow="none" className="border border-divider">
          <CardHeader className="pb-0">
            <span className="text-tiny font-semibold uppercase tracking-wider text-default-500">
              {t("orderDetails.serviceDetails")}
            </span>
          </CardHeader>
          <CardBody className="space-y-2 text-sm">
            {service && <p>{service}</p>}
            {location && (
              <div>
                <p className="font-medium">
                  {t("orderDetails.plannedDropoff")}
                </p>
                {location.name && <p>{location.name}</p>}
                <p>
                  {locationAddress || t("orderDetails.locationNotRecorded")}
                </p>
              </div>
            )}
            {location && date(plan?.handoffBy) && (
              <p>
                {t("orderDetails.dropoffBy")}: {date(plan?.handoffBy)}
              </p>
            )}
            {plan?.inductionKind === "PICKUP" &&
              date(plan.pickupDate, "DATE") && (
                <p>
                  {t("orderDetails.pickupDate")}:{" "}
                  {date(plan.pickupDate, "DATE")}
                </p>
              )}
            {firstDelivery && (
              <p>
                {t("orderDetails.estimatedDelivery")}: {firstDelivery}
                {lastDelivery && lastDelivery !== firstDelivery
                  ? ` – ${lastDelivery}`
                  : ""}
              </p>
            )}
            {shipment.options?.signatureRequired && (
              <p>{t("orderDetails.signatureRequired")}</p>
            )}
            {shipment.options?.insurance && (
              <p>{t("orderDetails.insuranceRequested")}</p>
            )}
          </CardBody>
        </Card>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        <Card shadow="none" className="border border-divider">
          <CardHeader className="pb-0">
            <div className="flex items-center gap-2">
              <Chip size="sm" variant="bordered" color="default">
                {t("orderDetails.from")}
              </Chip>
              <span className="text-tiny font-semibold uppercase tracking-wider text-default-500">
                {t("orderDetails.sender")}
              </span>
            </div>
          </CardHeader>
          <CardBody className="space-y-1">
            <p className="font-medium">{sender?.name ?? "—"}</p>
            {sender?.company && <p>{sender.company}</p>}
            {sender?.fullAddress && (
              <p className="text-small text-default-500">
                {sender.fullAddress}
              </p>
            )}
            {sender?.phone && (
              <p className="text-small text-default-500">{sender.phone}</p>
            )}
            {sender?.instructions && <p>{sender.instructions}</p>}
            {sender?.email && (
              <p className="text-small text-default-500">{sender.email}</p>
            )}
          </CardBody>
        </Card>

        <Card shadow="none" className="border border-divider">
          <CardHeader className="pb-0">
            <div className="flex items-center gap-2">
              <Chip size="sm" color="primary" variant="flat">
                {t("orderDetails.to")}
              </Chip>
              <span className="text-tiny font-semibold uppercase tracking-wider text-default-500">
                {t("orderDetails.recipient")}
              </span>
            </div>
          </CardHeader>
          <CardBody className="space-y-1">
            <p className="font-medium">{recipient?.name ?? "—"}</p>
            {recipient?.company && <p>{recipient.company}</p>}
            {recipient?.fullAddress && (
              <p className="text-small text-default-500">
                {recipient.fullAddress}
              </p>
            )}
            {recipient?.phone && (
              <p className="text-small text-default-500">{recipient.phone}</p>
            )}
            {recipient?.instructions && <p>{recipient.instructions}</p>}
            {recipient?.email && (
              <p className="text-small text-default-500">{recipient.email}</p>
            )}
          </CardBody>
        </Card>
      </div>

      {shipment.deliveryNotes &&
        shipment.deliveryNotes !== recipient?.instructions && (
          <Card shadow="none" className="border border-divider">
            <CardHeader className="pb-0">
              <span className="text-tiny font-semibold uppercase tracking-wider text-default-500">
                {t("orderDetails.deliveryInstructions")}
              </span>
            </CardHeader>
            <CardBody>
              <p className="whitespace-pre-wrap break-words">
                {shipment.deliveryNotes}
              </p>
            </CardBody>
          </Card>
        )}
      {returnParty && (
        <Card shadow="none" className="border border-divider">
          <CardHeader className="pb-0">
            <span className="text-tiny font-semibold uppercase tracking-wider text-default-500">
              {t("orderDetails.returnAddress")}
            </span>
          </CardHeader>
          <CardBody className="space-y-1">
            {returnParty.name && (
              <p className="font-medium">{returnParty.name}</p>
            )}
            {returnParty.company && <p>{returnParty.company}</p>}
            {returnParty.fullAddress && (
              <p className="text-small text-default-500">
                {returnParty.fullAddress}
              </p>
            )}
            {returnParty.phone && (
              <p className="text-small text-default-500">{returnParty.phone}</p>
            )}
            {returnParty.email && (
              <p className="text-small text-default-500">{returnParty.email}</p>
            )}
            {returnParty.instructions && <p>{returnParty.instructions}</p>}
          </CardBody>
        </Card>
      )}
      {packages.length > 0 && (
        <Card shadow="none" className="border border-divider">
          <CardHeader className="pb-0">
            <span className="text-tiny font-semibold uppercase tracking-wider text-default-500">
              {t("orderDetails.packagesCount", { count: packages.length })}
            </span>
          </CardHeader>
          <CardBody>
            <div className="space-y-2">
              {packages.map((pkg: ShipmentPackage, index: number) => (
                <div
                  key={pkg.id ?? index}
                  className="rounded-medium bg-default-50 p-3"
                >
                  <p className="font-medium">
                    {t("orderDetails.package", { number: index + 1 })}
                  </p>
                  {pkg.description && (
                    <p className="whitespace-pre-wrap break-words">
                      {pkg.description}
                    </p>
                  )}
                  {pkg.packageTypeDisplay?.availability ===
                    "RETAINED_DISPLAY" && (
                    <p>{pkg.packageTypeDisplay.value.name}</p>
                  )}
                  {pkg.declaredValueCents != null &&
                    pkg.declaredValueCents > 0 && (
                      <p>
                        {t("orderDetails.declaredValue")}:{" "}
                        {declaredMoney(pkg.declaredValueCents)}
                      </p>
                    )}
                  {(pkg.isFragile || pkg.requiresCooling) && (
                    <p>
                      {[
                        pkg.isFragile ? t("orderDetails.fragile") : null,
                        pkg.requiresCooling
                          ? t("orderDetails.requiresCooling")
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  )}

                  <p className="text-small text-default-500">
                    {pkg.weightGrams
                      ? `${(pkg.weightGrams / 1000).toFixed(1)} kg`
                      : "—"}
                    {pkg.lengthCm &&
                      pkg.widthCm &&
                      pkg.heightCm &&
                      ` · ${pkg.lengthCm} × ${pkg.widthCm} × ${pkg.heightCm} cm`}
                  </p>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}
      <ShipmentRevisionHistory
        updatedAt={shipment.updatedAt}
        shipmentId={shipment.id}
      />
    </section>
  );
}
